import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Send Email Step
 *
 * Generic, reusable step that sends a pre-rendered email to a list of
 * recipients via Microsoft Graph. It receives the fully rendered subject
 * and HTML content as inputs — no template lookup or rendering logic.
 *
 * Upstream steps (e.g. render_order_approval_email) are responsible for
 * preparing the email content before wiring it into this step.
 */
export const sendEmailStep = defineStepConfig({
  type: "send_email",
  name: "Send Email",
  description:
    "Sends a pre-rendered email to recipients via Microsoft Graph. Receives subject and HTML content as inputs.",
  category: "Integration",

  inputSchema: z.object({
    to: z.array(z.string()).describe("Primary recipient email addresses"),
    cc: z.array(z.string()).optional().describe("CC recipient email addresses"),
    bcc: z
      .array(z.string())
      .optional()
      .describe("BCC recipient email addresses"),
    replyTo: z
      .array(z.string())
      .optional()
      .describe("Reply-To email addresses"),
    subject: z.string().describe("The email subject line"),
    htmlContent: z.string().describe("The rendered HTML email body"),
    attachmentDocumentIds: z
      .array(z.string())
      .optional()
      .describe(
        "Document IDs to attach as inline files (base64 via Microsoft Graph). " +
          "Total size must stay under the 3 MB inline limit. Wire from an " +
          "upstream step that produces documents, e.g. web_browser downloads.",
      ),
    idempotencyKeyPrefix: z
      .string()
      .optional()
      .describe(
        "Optional prefix for idempotency key (e.g., 'approval:staged_po_123:watch_role:l1'). " +
          "If provided, deduplication is based on this prefix + recipient. " +
          "If not provided, defaults to workflow run + step run context.",
      ),
    forwardFromGraphMessageId: z
      .string()
      .nullish()
      .describe(
        "When set, the email is sent as a forward of this Microsoft Graph message ID. " +
          "Graph re-attaches the original message's attachments server-side, so the " +
          "rendered HTML is prepended above the quoted original. The message must live " +
          "in the senderMailbox configured on this step.",
      ),
  }),

  outputSchema: z.object({
    sent: z
      .boolean()
      .describe(
        "True only if THIS run actually dispatched an email. False when the " +
          "send was skipped because a previous run with the same idempotency " +
          "key already delivered the message — see `skipped` for that case.",
      ),
    skipped: z
      .boolean()
      .default(false)
      .describe(
        "True when the send was suppressed by the idempotency check (an " +
          "earlier run already delivered the same message). The recipient " +
          "still received the email — just not from this run.",
      ),
    recipientCount: z
      .number()
      .describe("Number of recipients the email was addressed to"),
    error: z.string().optional().describe("Error message if sending failed"),
  }),

  configSchema: z.object({
    senderMailbox: z
      .string()
      .describe(
        "The mailbox UPN (e.g. 'sales@company.com') to send from via Microsoft Graph",
      ),
    emailTopic: z
      .string()
      .optional()
      .describe(
        "Optional topic tag for the outbound email record (e.g. 'sales_order_approved')",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "60 seconds",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
  },
});
